import React, { useRef, useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { collection, addDoc, serverTimestamp, doc, onSnapshot } from 'firebase/firestore';
import { db } from '../firebase/config';
import Header from '../components/Header';
import Footer from '../components/Footer';
import Toast from '../components/Toast';
import MapComponent from '../components/MapComponent';
import Breadcrumbs from '../components/Breadcrumbs';
import ResourceDirectory from '../components/ResourceDirectory';
import { normalizePhone, normalizeEmail, validateLatLng } from '../utils/validationUtils';

const GetHelp = () => {
  const [step, setStep] = useState(1);
  const [toast, setToast] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [availability, setAvailability] = useState(null);
  const submittedRef = useRef(false);
  const navigate = useNavigate();

  // Listen to availability status
  React.useEffect(() => {
    const unsubAvail = onSnapshot(doc(db, 'availability', 'settings'), (snap) => {
      if (snap.exists()) {
        const data = snap.data();
        let showBanner = true;
        if (data.expirationDate) {
          const expireTime = data.expirationDate.toDate ? data.expirationDate.toDate() : new Date(data.expirationDate);
          if (new Date() > expireTime) {
            showBanner = false;
          }
        }
        setAvailability({
          ...data,
          showBanner: showBanner && data.status !== 'normal'
        });
      } else {
        setAvailability(null);
      }
    });

    return () => unsubAvail();
  }, []);

  // Reference number generated once per form load
  const [refNumber] = useState(() => 'GH-' + Math.random().toString(36).substring(2, 8).toUpperCase());

  // Form states
  const [locationDesc, setLocationDesc] = useState('');
  const [landmark, setLandmark] = useState('');
  const [latitude, setLatitude] = useState(null);
  const [longitude, setLongitude] = useState(null);

  // Demographics
  const [adultsCount, setAdultsCount] = useState(1);
  const [childrenCount, setChildrenCount] = useState(0);
  const [petsPresent, setPetsPresent] = useState(false);
  const [accessibilityNeeds, setAccessibilityNeeds] = useState('');
  const [safetyConcern, setSafetyConcern] = useState(false);

  // Categories
  const [tentNeeded, setTentNeeded] = useState(false);
  const [sleepingBagNeeded, setSleepingBagNeeded] = useState(false);
  const [foodNeeded, setFoodNeeded] = useState(false);
  const [waterNeeded, setWaterNeeded] = useState(false);
  const [hygieneNeeded, setHygieneNeeded] = useState(false);
  const [clothingNeeded, setClothingNeeded] = useState(false);
  const [clothingSizes, setClothingSizes] = useState('');
  const [seasonalSupplies, setSeasonalSupplies] = useState(false);
  const [referralRequested, setReferralRequested] = useState(false);

  // Preferences & details
  const [bestTimeToVisit, setBestTimeToVisit] = useState('');
  const [permissionToApproach, setPermissionToApproach] = useState(true);
  const [preferredContact, setPreferredContact] = useState('none');
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [permissionToText, setPermissionToText] = useState(false);
  const [additionalDetails, setAdditionalDetails] = useState('');
  const [urgency, setUrgency] = useState('as_soon_as_available');
  const [fieldErrors, setFieldErrors] = useState({});

  const DRAFT_KEY = 'tentfinder_gethelp_draft';

  useEffect(() => {
    try {
      const saved = localStorage.getItem(DRAFT_KEY);
      if (saved) {
        const d = JSON.parse(saved);
        if (d.locationDesc) setLocationDesc(d.locationDesc);
        if (d.landmark) setLandmark(d.landmark);
        if (d.latitude) setLatitude(d.latitude);
        if (d.longitude) setLongitude(d.longitude);
        if (d.adultsCount !== undefined) setAdultsCount(d.adultsCount);
        if (d.childrenCount !== undefined) setChildrenCount(d.childrenCount);
        if (d.petsPresent !== undefined) setPetsPresent(d.petsPresent);
        if (d.accessibilityNeeds) setAccessibilityNeeds(d.accessibilityNeeds);
        if (d.safetyConcern !== undefined) setSafetyConcern(d.safetyConcern);
        if (d.tentNeeded !== undefined) setTentNeeded(d.tentNeeded);
        if (d.sleepingBagNeeded !== undefined) setSleepingBagNeeded(d.sleepingBagNeeded);
        if (d.foodNeeded !== undefined) setFoodNeeded(d.foodNeeded);
        if (d.waterNeeded !== undefined) setWaterNeeded(d.waterNeeded);
        if (d.hygieneNeeded !== undefined) setHygieneNeeded(d.hygieneNeeded);
        if (d.clothingNeeded !== undefined) setClothingNeeded(d.clothingNeeded);
        if (d.clothingSizes) setClothingSizes(d.clothingSizes);
        if (d.preferredContact) setPreferredContact(d.preferredContact);
        if (d.phone) setPhone(d.phone);
        if (d.email) setEmail(d.email);
        if (d.additionalDetails) setAdditionalDetails(d.additionalDetails);
        if (d.step) setStep(d.step);
      }
    } catch (e) {
      console.warn('Could not restore GetHelp draft:', e);
    }
  }, []);

  useEffect(() => {
    if (submittedRef.current) return;
    try {
      const draft = {
        locationDesc, landmark, latitude, longitude,
        adultsCount, childrenCount, petsPresent, accessibilityNeeds, safetyConcern,
        tentNeeded, sleepingBagNeeded, foodNeeded, waterNeeded, hygieneNeeded, clothingNeeded, clothingSizes,
        preferredContact, phone, email, additionalDetails, step
      };
      localStorage.setItem(DRAFT_KEY, JSON.stringify(draft));
    } catch (e) {}
  }, [locationDesc, landmark, latitude, longitude, adultsCount, childrenCount, petsPresent, accessibilityNeeds, safetyConcern, tentNeeded, sleepingBagNeeded, foodNeeded, waterNeeded, hygieneNeeded, clothingNeeded, clothingSizes, preferredContact, phone, email, additionalDetails, step]);

  const clearDraft = () => {
    try {
      localStorage.removeItem(DRAFT_KEY);
    } catch (e) {}
    setLocationDesc('');
    setLandmark('');
    setLatitude(null);
    setLongitude(null);
    setPhone('');
    setEmail('');
    setStep(1);
  };

  const handleLocationSelect = (lat, lng) => {
    setLatitude(lat);
    setLongitude(lng);
    setFieldErrors((prev) => ({ ...prev, coords: null }));
  };

  const validateStep1 = () => {
    const errors = {};
    if (!locationDesc.trim()) {
      errors.locationDesc = 'Please provide a location description.';
    }
    if (!validateLatLng(latitude, longitude)) {
      errors.coords = 'Coordinates must both be empty or a valid latitude (-90 to 90) and longitude (-180 to 180).';
    }
    setFieldErrors(errors);
    if (Object.keys(errors).length > 0) {
      const firstErr = Object.values(errors)[0];
      setToast({ message: firstErr, type: 'error' });
      return false;
    }
    return true;
  };

  const validateStep4 = () => {
    const errors = {};
    if (preferredContact === 'phone' && !phone.trim()) {
      errors.phone = 'Please enter your phone number since Phone was selected as preferred contact method.';
    }
    if (preferredContact === 'email' && !email.trim()) {
      errors.email = 'Please enter your email address since Email was selected as preferred contact method.';
    } else if (email.trim() && !email.includes('@')) {
      errors.email = 'Please enter a valid email address.';
    }
    setFieldErrors(errors);
    if (Object.keys(errors).length > 0) {
      const firstErr = Object.values(errors)[0];
      setToast({ message: firstErr, type: 'error' });
      return false;
    }
    return true;
  };

  const handleNext = (e) => {
    e.preventDefault();
    if (step === 1 && !validateStep1()) return;
    setStep((prev) => prev + 1);
  };

  const handlePrev = (e) => {
    e.preventDefault();
    setStep((prev) => prev - 1);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (submitting || submittedRef.current) return;
    if (!validateStep4()) return;

    setSubmitting(true);
    submittedRef.current = true;

    // Collect requested assistance categories
    const requestedCategories = [];
    if (tentNeeded) requestedCategories.push('tent');
    if (sleepingBagNeeded) requestedCategories.push('sleeping_bag');
    if (foodNeeded) requestedCategories.push('food');
    if (waterNeeded) requestedCategories.push('water');
    if (hygieneNeeded) requestedCategories.push('hygiene');
    if (clothingNeeded) requestedCategories.push('clothing');
    if (seasonalSupplies) requestedCategories.push('seasonal_supplies');
    if (referralRequested) requestedCategories.push('referral');

    const cleanEmail = normalizeEmail(email);
    const cleanPhone = normalizePhone(phone);

    try {
      await addDoc(collection(db, 'help_requests'), {
        referenceNumber: refNumber,
        type: 'help_request',
        status: 'new',
        locationDesc: locationDesc.trim(),
        landmark: landmark.trim() || null,
        latitude: latitude,
        longitude: longitude,
        adultsCount: parseInt(adultsCount, 10) || 1,
        childrenCount: Math.max(0, parseInt(childrenCount, 10) || 0),
        petsPresent: !!petsPresent,
        accessibilityNeeds: accessibilityNeeds.trim() || null,
        safetyConcern: !!safetyConcern,
        requestedCategories: requestedCategories,
        clothingSizes: clothingNeeded ? clothingSizes.trim() || null : null,
        bestTimeToVisit: bestTimeToVisit.trim() || null,
        permissionToApproach: !!permissionToApproach,
        preferredContact: preferredContact,
        phone: cleanPhone || null,
        email: cleanEmail || null,
        permissionToText: !!permissionToText,
        additionalDetails: additionalDetails.trim() || null,
        urgency: urgency,
        timestamp: serverTimestamp(),
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        schemaVersion: 2,
        notificationStatus: 'pending'
      });

      setToast({
        message: 'Your request was received. We review requests as availability allows, but this is not an emergency or continuously monitored service. If we can assist or provide useful resource information, we will contact you using the method you selected.',
        type: 'success',
      });

      // Clear states & draft
      try {
        localStorage.removeItem(DRAFT_KEY);
      } catch (e) {}
      setLocationDesc('');
      setLandmark('');
      setLatitude(null);
      setLongitude(null);

      setTimeout(() => {
        navigate('/');
      }, 5000);
    } catch (error) {
      console.error('Error submitting help request:', error);
      submittedRef.current = false;
      setToast({ message: 'Error submitting your request. Please try again or call 211.', type: 'error' });
    } finally {
      setSubmitting(false);
    }
  };

  const getInputClass = (hasError) =>
    `w-full px-4 py-2.5 bg-charcoal-card border ${
      hasError ? 'border-urgent-orange ring-1 ring-urgent-orange' : 'border-white/10'
    } rounded-lg text-white placeholder-slate-500 focus:ring-2 focus:ring-urgent-orange focus:border-transparent resize-none`;

  const inputClass = getInputClass(false);

  return (
    <div className="min-h-screen flex flex-col bg-charcoal-dark text-slate-200">
      <Header />
      <Breadcrumbs />

      <main id="main-content" className="flex-grow max-w-2xl mx-auto w-full px-4 md:px-8 py-8">
        <div className="chrome-panel border border-white/5 rounded-lg p-6 md:p-8">
          
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-2xl md:text-3xl font-bold text-white uppercase tracking-tight">
              Get Help
            </h2>
            <span className="text-xs font-mono text-slate-500 bg-charcoal-dark px-2 py-1 rounded">
              Ref: {refNumber}
            </span>
          </div>

          {/* Emergency Alert notices */}
          <div className="mb-6 rounded-lg border border-urgent-orange/40 bg-urgent-orange/10 px-4 py-3 text-sm" role="note">
            <p className="text-white font-bold uppercase tracking-wide text-xs mb-1">
              🚨 Life Safety Warning
            </p>
            <p className="text-slate-300 leading-relaxed mb-2">
              Tent Finder is operated by Not By Chance Outreach, a small nonprofit run by volunteers. Requests are reviewed as availability allows and are not monitored continuously. We cannot guarantee a response time or that every requested item will be available.
            </p>
            <p className="text-slate-300 leading-relaxed">
              If you are in immediate danger, call{' '}
              <a href="tel:911" className="text-urgent-orange font-bold hover:underline">911</a>. For immediate community-resource information, call{' '}
              <a href="tel:211" className="text-banana-yellow font-bold hover:underline">211</a>.
            </p>
          </div>

          {/* Privacy Disclaimer */}
          <p className="text-slate-400 mb-6 text-xs leading-relaxed">
            You do not have to provide your name. We collect the information you submit, which may include your location and optional contact information, solely to coordinate outreach. Access is limited to authorized outreach staff. Read our full{' '}
            <Link to="/privacy" className="text-banana-yellow hover:underline font-semibold">Privacy Policy</Link>.
          </p>

          {/* Availability Alert Banner */}
          {availability && availability.showBanner && (
            <div className={`mb-6 p-4 rounded-lg border text-xs leading-relaxed ${
              availability.status === 'unavailable' 
                ? 'bg-red-500/10 border-red-500/30 text-red-200' 
                : 'bg-yellow-500/10 border-yellow-500/30 text-yellow-200'
            }`}>
              <span className="font-bold uppercase tracking-wider block mb-1">
                ⚠️ Outreach Notice: {availability.status === 'unavailable' ? 'Service Temporarily Offline' : 'Response Times May Be Delayed'}
              </span>
              <p>{availability.message}</p>
              {availability.expectedReviewDate && (
                <p className="text-[10px] text-slate-500 mt-1 font-bold">
                  Expected review time: {new Date(availability.expectedReviewDate.toDate ? availability.expectedReviewDate.toDate() : availability.expectedReviewDate).toLocaleString()}
                </p>
              )}
            </div>
          )}

          {!(availability && availability.acceptHelpRequests === true) ? (
            <div className="bg-charcoal-card border border-red-500/30 p-6 rounded-lg text-center space-y-4">
              <p className="text-red-200 font-bold uppercase tracking-wider text-xs">
                Request Intake is Currently Closed
              </p>
              <p className="text-slate-400 text-xs leading-relaxed">
                We are temporarily unable to accept new help requests online. Please use the resources listed above or contact emergency services if you need immediate assistance.
              </p>
            </div>
          ) : (
            <>
              {/* Steps Indicator */}
              <div className="flex justify-between items-center mb-8 bg-charcoal-card border border-white/5 p-3 rounded-lg text-xs">
                <span className={step === 1 ? 'text-urgent-orange font-bold' : 'text-slate-500'}>1. Location</span>
                <span className="text-slate-600">➔</span>
                <span className={step === 2 ? 'text-urgent-orange font-bold' : 'text-slate-500'}>2. Profile</span>
                <span className="text-slate-600">➔</span>
                <span className={step === 3 ? 'text-urgent-orange font-bold' : 'text-slate-500'}>3. Needs</span>
                <span className="text-slate-600">➔</span>
                <span className={step === 4 ? 'text-urgent-orange font-bold' : 'text-slate-500'}>4. Contact</span>
              </div>

              <form onSubmit={handleSubmit} className="space-y-6">
            
            {/* STEP 1: LOCATION DETAILS */}
            {step === 1 && (
              <div className="space-y-6">
                <div>
                  <label htmlFor="locationDesc" className="block text-sm font-bold text-slate-200 uppercase tracking-wider mb-2">
                    Location Description *
                  </label>
                  <textarea
                    id="locationDesc"
                    value={locationDesc}
                    onChange={(e) => {
                      setLocationDesc(e.target.value);
                      if (fieldErrors.locationDesc) setFieldErrors((prev) => ({ ...prev, locationDesc: null }));
                    }}
                    rows="3"
                    maxLength={2000}
                    className={getInputClass(!!fieldErrors.locationDesc)}
                    placeholder="Describe where you are (e.g. In the woods behind the Mount Washington Walmart, near the blue tent)."
                    required
                    aria-required="true"
                    aria-invalid={!!fieldErrors.locationDesc}
                    aria-describedby="locationDesc-help"
                  />
                  {fieldErrors.locationDesc && (
                    <p id="locationDesc-error" className="text-xs text-urgent-orange font-bold mt-1" role="alert">
                      ⚠️ {fieldErrors.locationDesc}
                    </p>
                  )}
                  <div id="locationDesc-help" className="flex justify-between text-[11px] text-slate-500 mt-1">
                    <span>Specific descriptions help our team locate you. Name is never required.</span>
                    <span aria-live="polite">{locationDesc.length}/2000</span>
                  </div>
                </div>

                <div>
                  <label htmlFor="landmark" className="block text-sm font-bold text-slate-200 uppercase tracking-wider mb-2">
                    Nearby Landmark / Intersection
                  </label>
                  <input
                    type="text"
                    id="landmark"
                    value={landmark}
                    onChange={(e) => setLandmark(e.target.value)}
                    maxLength={200}
                    className={inputClass}
                    placeholder="e.g. Corner of Route 44 and Joe B Hall Hwy"
                  />
                </div>

                <div>
                  <label className="block text-sm font-bold text-slate-200 uppercase tracking-wider mb-2">
                    Pin Location on Map
                  </label>
                  <MapComponent mode="select" onLocationSelect={handleLocationSelect} />
                  {latitude != null && longitude != null && (
                    <div className="mt-2 text-xs text-hope-green font-medium">
                      📍 Coordinates pinned: {latitude.toFixed(6)}, {longitude.toFixed(6)}
                    </div>
                  )}
                </div>

                <div className="pt-4 border-t border-white/5 flex justify-between">
                  <div className="flex gap-2">
                    <button
                      type="button"
                      onClick={clearDraft}
                      className="bg-white/5 hover:bg-red-500/20 text-slate-400 hover:text-red-300 font-bold py-2.5 px-3 rounded-lg transition-colors text-xs uppercase"
                      title="Clear draft entries and reset form"
                    >
                      Reset
                    </button>
                    <button
                      type="button"
                      onClick={() => navigate('/')}
                      className="bg-white/5 hover:bg-white/10 text-white font-bold py-2.5 px-6 rounded-lg transition-colors text-xs uppercase"
                    >
                      Cancel
                    </button>
                  </div>
                  <button
                    type="button"
                    onClick={handleNext}
                    className="cyber-btn-orange py-2 px-6 text-xs uppercase"
                  >
                    Next Step
                  </button>
                </div>
              </div>
            )}

            {/* STEP 2: PROFILE & HOUSEHOLD */}
            {step === 2 && (
              <div className="space-y-6">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="adultsCount" className="block text-sm font-bold text-slate-200 uppercase tracking-wider mb-2">
                      Number of Adults
                    </label>
                    <input
                      type="number"
                      id="adultsCount"
                      min="1"
                      max="20"
                      value={adultsCount}
                      onChange={(e) => setAdultsCount(e.target.value)}
                      className={inputClass}
                    />
                  </div>
                  <div>
                    <label htmlFor="childrenCount" className="block text-sm font-bold text-slate-200 uppercase tracking-wider mb-2">
                      Number of Children
                    </label>
                    <input
                      type="number"
                      id="childrenCount"
                      min="0"
                      max="20"
                      value={childrenCount}
                      onChange={(e) => setChildrenCount(e.target.value)}
                      className={inputClass}
                    />
                  </div>
                </div>

                <label className="flex items-center space-x-3 cursor-pointer hover:bg-white/5 p-3 rounded border border-white/5">
                  <input
                    type="checkbox"
                    checked={petsPresent}
                    onChange={(e) => setPetsPresent(e.target.checked)}
                    className="w-5 h-5 text-urgent-orange border-gray-300 rounded focus:ring-urgent-orange"
                  />
                  <div>
                    <span className="text-sm font-semibold text-slate-200 block">Pets Present</span>
                    <span className="text-xs text-slate-500">Checking this helps outreach workers prepare for any animals at the location.</span>
                  </div>
                </label>

                <div>
                  <label htmlFor="accessibilityNeeds" className="block text-sm font-bold text-slate-200 uppercase tracking-wider mb-2">
                    Accessibility or Mobility Needs
                  </label>
                  <textarea
                    id="accessibilityNeeds"
                    value={accessibilityNeeds}
                    onChange={(e) => setAccessibilityNeeds(e.target.value)}
                    rows="2"
                    maxLength={1000}
                    className={inputClass}
                    placeholder="e.g. Wheelchair user, difficulty walking, limited vision, etc."
                  />
                </div>

                <label className="flex items-center space-x-3 cursor-pointer hover:bg-white/5 p-3 rounded border border-white/5">
                  <input
                    type="checkbox"
                    checked={safetyConcern}
                    onChange={(e) => setSafetyConcern(e.target.checked)}
                    className="w-5 h-5 text-urgent-orange border-gray-300 rounded focus:ring-urgent-orange"
                  />
                  <div>
                    <span className="text-sm font-semibold text-slate-200 block">Immediate Safety Concern</span>
                    <span className="text-xs text-slate-500">Check this if you have concerns about personal safety (e.g. wildlife, environment, harassment). Not emergency dispatch.</span>
                  </div>
                </label>

                <div className="pt-4 border-t border-white/5 flex justify-between">
                  <button
                    type="button"
                    onClick={handlePrev}
                    className="bg-white/5 hover:bg-white/10 text-white font-bold py-2.5 px-6 rounded-lg transition-colors text-xs uppercase"
                  >
                    Back
                  </button>
                  <button
                    type="button"
                    onClick={handleNext}
                    className="cyber-btn-orange py-2 px-6 text-xs uppercase"
                  >
                    Next Step
                  </button>
                </div>
              </div>
            )}

            {/* STEP 3: ASSISTANCE CATEGORIES */}
            {step === 3 && (
              <div className="space-y-6">
                <fieldset>
                  <legend className="block text-sm font-bold text-slate-200 uppercase tracking-wider mb-3">
                    Requested Assistance (Select all needed items)
                  </legend>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" role="group" aria-label="Assistance categories">
                    <label className="flex items-center space-x-3 p-3 rounded bg-charcoal-card border border-white/5 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={tentNeeded}
                        onChange={(e) => setTentNeeded(e.target.checked)}
                        className="w-5 h-5 text-urgent-orange border-gray-300 rounded focus:ring-urgent-orange"
                      />
                      <span className="text-sm text-slate-200">Tent Needed</span>
                    </label>

                    <label className="flex items-center space-x-3 p-3 rounded bg-charcoal-card border border-white/5 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={sleepingBagNeeded}
                        onChange={(e) => setSleepingBagNeeded(e.target.checked)}
                        className="w-5 h-5 text-urgent-orange border-gray-300 rounded focus:ring-urgent-orange"
                      />
                      <span className="text-sm text-slate-200">Sleeping Bag / Blanket</span>
                    </label>

                    <label className="flex items-center space-x-3 p-3 rounded bg-charcoal-card border border-white/5 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={foodNeeded}
                        onChange={(e) => setFoodNeeded(e.target.checked)}
                        className="w-5 h-5 text-urgent-orange border-gray-300 rounded focus:ring-urgent-orange"
                      />
                      <span className="text-sm text-slate-200">Food Needed</span>
                    </label>

                    <label className="flex items-center space-x-3 p-3 rounded bg-charcoal-card border border-white/5 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={waterNeeded}
                        onChange={(e) => setWaterNeeded(e.target.checked)}
                        className="w-5 h-5 text-urgent-orange border-gray-300 rounded focus:ring-urgent-orange"
                      />
                      <span className="text-sm text-slate-200">Water Needed</span>
                    </label>

                    <label className="flex items-center space-x-3 p-3 rounded bg-charcoal-card border border-white/5 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={hygieneNeeded}
                        onChange={(e) => setHygieneNeeded(e.target.checked)}
                        className="w-5 h-5 text-urgent-orange border-gray-300 rounded focus:ring-urgent-orange"
                      />
                      <span className="text-sm text-slate-200">Hygiene Products</span>
                    </label>

                    <label className="flex items-center space-x-3 p-3 rounded bg-charcoal-card border border-white/5 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={clothingNeeded}
                        onChange={(e) => setClothingNeeded(e.target.checked)}
                        className="w-5 h-5 text-urgent-orange border-gray-300 rounded focus:ring-urgent-orange"
                      />
                      <span className="text-sm text-slate-200">Clothing / Socks</span>
                    </label>

                    <label className="flex items-center space-x-3 p-3 rounded bg-charcoal-card border border-white/5 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={seasonalSupplies}
                        onChange={(e) => setSeasonalSupplies(e.target.checked)}
                        className="w-5 h-5 text-urgent-orange border-gray-300 rounded focus:ring-urgent-orange"
                      />
                      <span className="text-sm text-slate-200">Seasonal Survival Supplies</span>
                    </label>

                    <label className="flex items-center space-x-3 p-3 rounded bg-charcoal-card border border-white/5 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={referralRequested}
                        onChange={(e) => setReferralRequested(e.target.checked)}
                        className="w-5 h-5 text-urgent-orange border-gray-300 rounded focus:ring-urgent-orange"
                      />
                      <span className="text-sm text-slate-200">Info / Referral Requested</span>
                    </label>
                  </div>
                </fieldset>

                {clothingNeeded && (
                  <div>
                    <label htmlFor="clothingSizes" className="block text-sm font-bold text-slate-200 uppercase tracking-wider mb-2">
                      Requested Clothing Sizes
                    </label>
                    <input
                      type="text"
                      id="clothingSizes"
                      value={clothingSizes}
                      onChange={(e) => setClothingSizes(e.target.value)}
                      maxLength={200}
                      className={inputClass}
                      placeholder="e.g. Mens socks L, Jacket XL"
                    />
                  </div>
                )}

                <div className="pt-4 border-t border-white/5 flex justify-between">
                  <button
                    type="button"
                    onClick={handlePrev}
                    className="bg-white/5 hover:bg-white/10 text-white font-bold py-2.5 px-6 rounded-lg transition-colors text-xs uppercase"
                  >
                    Back
                  </button>
                  <button
                    type="button"
                    onClick={handleNext}
                    className="cyber-btn-orange py-2 px-6 text-xs uppercase"
                  >
                    Next Step
                  </button>
                </div>
              </div>
            )}

            {/* STEP 4: CONTACT PREFERENCES & SUBMISSION */}
            {step === 4 && (
              <div className="space-y-6">
                <div>
                  <label htmlFor="preferredContact" className="block text-sm font-bold text-slate-200 uppercase tracking-wider mb-2">
                    Preferred Contact Method
                  </label>
                  <select
                    id="preferredContact"
                    value={preferredContact}
                    onChange={(e) => setPreferredContact(e.target.value)}
                    className={inputClass}
                  >
                    <option value="none">Do not contact me (Outreach visit only)</option>
                    <option value="phone">Phone Call or Text</option>
                    <option value="email">Email</option>
                  </select>
                </div>

                {preferredContact === 'phone' && (
                  <div className="space-y-4">
                    <div>
                      <label htmlFor="phone" className="block text-sm font-bold text-slate-200 uppercase tracking-wider mb-2">
                        Phone Number *
                      </label>
                      <input
                        type="tel"
                        id="phone"
                        value={phone}
                        onChange={(e) => {
                          setPhone(e.target.value);
                          if (fieldErrors.phone) setFieldErrors((prev) => ({ ...prev, phone: null }));
                        }}
                        maxLength={30}
                        className={getInputClass(!!fieldErrors.phone)}
                        placeholder="(555) 123-4567"
                        required
                        aria-invalid={!!fieldErrors.phone}
                      />
                      {fieldErrors.phone && (
                        <p className="text-xs text-urgent-orange font-bold mt-1" role="alert">
                          ⚠️ {fieldErrors.phone}
                        </p>
                      )}
                    </div>
                    <label className="flex items-center space-x-3 cursor-pointer p-2 rounded hover:bg-white/5">
                      <input
                        type="checkbox"
                        checked={permissionToText}
                        onChange={(e) => setPermissionToText(e.target.checked)}
                        className="w-5 h-5 text-urgent-orange border-gray-300 rounded focus:ring-urgent-orange"
                      />
                      <span className="text-xs text-slate-300">I give permission to receive text messages for outreach coordination.</span>
                    </label>
                  </div>
                )}

                {preferredContact === 'email' && (
                  <div>
                    <label htmlFor="email" className="block text-sm font-bold text-slate-200 uppercase tracking-wider mb-2">
                      Email Address *
                    </label>
                    <input
                      type="email"
                      id="email"
                      value={email}
                      onChange={(e) => {
                        setEmail(e.target.value);
                        if (fieldErrors.email) setFieldErrors((prev) => ({ ...prev, email: null }));
                      }}
                      maxLength={100}
                      className={getInputClass(!!fieldErrors.email)}
                      placeholder="name@example.com"
                      required
                      aria-invalid={!!fieldErrors.email}
                    />
                    {fieldErrors.email && (
                      <p className="text-xs text-urgent-orange font-bold mt-1" role="alert">
                        ⚠️ {fieldErrors.email}
                      </p>
                    )}
                  </div>
                )}

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="bestTimeToVisit" className="block text-sm font-bold text-slate-200 uppercase tracking-wider mb-2">
                      Best Time to Visit
                    </label>
                    <input
                      type="text"
                      id="bestTimeToVisit"
                      value={bestTimeToVisit}
                      onChange={(e) => setBestTimeToVisit(e.target.value)}
                      maxLength={100}
                      className={inputClass}
                      placeholder="e.g. Mornings, Weekends"
                    />
                  </div>
                  <div>
                    <label htmlFor="urgency" className="block text-sm font-bold text-slate-200 uppercase tracking-wider mb-2">
                      Urgency
                    </label>
                    <select
                      id="urgency"
                      value={urgency}
                      onChange={(e) => setUrgency(e.target.value)}
                      className={inputClass}
                    >
                      <option value="as_soon_as_available">As soon as available</option>
                      <option value="within_a_few_days">Within a few days</option>
                      <option value="next_scheduled_outreach">Next scheduled outreach</option>
                      <option value="referral_only">Information / Referral only</option>
                    </select>
                  </div>
                </div>

                <label className="flex items-center space-x-3 cursor-pointer p-3 rounded bg-charcoal-card border border-white/5">
                  <input
                    type="checkbox"
                    checked={permissionToApproach}
                    onChange={(e) => setPermissionToApproach(e.target.checked)}
                    className="w-5 h-5 text-urgent-orange border-gray-300 rounded focus:ring-urgent-orange"
                  />
                  <div>
                    <span className="text-sm font-semibold text-slate-200 block">Outreach Approach Permission</span>
                    <span className="text-xs text-slate-500">I give permission for outreach workers to approach my tent/location.</span>
                  </div>
                </label>

                <div>
                  <label htmlFor="additionalDetails" className="block text-sm font-bold text-slate-200 uppercase tracking-wider mb-2">
                    Additional Details
                  </label>
                  <textarea
                    id="additionalDetails"
                    value={additionalDetails}
                    onChange={(e) => setAdditionalDetails(e.target.value)}
                    rows="3"
                    maxLength={2000}
                    className={inputClass}
                    placeholder="Any extra details that can help us find or assist you."
                  />
                </div>

                <div className="bg-charcoal-card/40 p-4 rounded-lg border border-white/5 text-xs text-slate-400 leading-relaxed">
                  <p className="font-bold text-white uppercase tracking-wider text-2xs mb-2">
                    Availability Disclaimer
                  </p>
                  <p>
                    Selecting an item does not guarantee availability. If we cannot provide it directly, we may provide information about another organization that can help.
                  </p>
                </div>

                <div className="pt-4 border-t border-white/5 flex flex-col sm:flex-row gap-4">
                  <button
                    type="button"
                    onClick={handlePrev}
                    className="flex-1 bg-white/5 hover:bg-white/10 text-white font-bold py-3 px-6 rounded-lg transition-colors text-xs uppercase"
                  >
                    Back
                  </button>
                  <button
                    type="submit"
                    disabled={submitting}
                    className="flex-1 cyber-btn-orange disabled:opacity-50 disabled:cursor-not-allowed uppercase text-xs"
                  >
                    {submitting ? 'Submitting...' : 'Submit Request'}
                  </button>
                </div>
              </div>
            )}
          </form>
          </>
          )}
        </div>

        <ResourceDirectory />
      </main>

      <Footer />

      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
        />
      )}
    </div>
  );
};

export default GetHelp;
